export function changeFramework(val, opts = { quiet: true }) {
  const platformState = this.getRootState()
  platformState[opts.quiet ? 'quietUpdate' : 'update']({ framework: val })
  this.call('setCookie', 'framework', val)
}

export function searchDocs({ pages, components }) {
  const ALLOWED_PAGES = ['docs', 'api', 'developers']
  const searchingPages = Object.entries(pages).reduce((acc, [key, value]) => {
    if (ALLOWED_PAGES.some((page) => key.includes(page))) {
      return [...acc, [key, value]]
    }
    return acc
  }, [])

  const search = (objectData = [], input = '', searchKey = false) =>
    objectData.filter(([parentKey, data]) => {
      if (!data) {
        return false
      }

      return Object.entries(data).some(
        ([key, value]) =>
          this.call('findSentencesWithWord', value, input).length > 0 ||
          (searchKey && (key.includes(input) || parentKey.includes(input)))
      )
    })

  const componentsMatched = search(
    Object.entries(components),
    this.state.search
  )

  if (this.state.search?.length < 3) {
    return
  }

  const pagesMatched = componentsMatched.reduce((acc, [key, value]) => {
    const pages2 = search(searchingPages, key, true)

    if (
      !pages2.length ||
      acc.some(([path]) => pages2.some(([pagePath]) => pagePath === path))
    ) {
      return acc
    }

    return [...acc, ...pages2.map((page) => page.concat({ [key]: value }))]
  }, [])

  this.state.replace({
    result: pagesMatched,
    loading: false,
    resultFor: this.state.search
  })
}

export function findSentencesWithWord(obj, searchWord) {
  const sentences = new Set()

  const findInString = (text, word) => {
    if (typeof text !== 'string') {
      return
    }

    const sentenceRegex = /[^.!?]+[.!?]+/gu
    const matchedSentences = text.match(sentenceRegex) || []

    matchedSentences.forEach((sentence) => {
      if (sentence.toLowerCase().includes(word.toLowerCase())) {
        sentences.add(sentence.trim())
      }
    })
  }

  const traverse = (data) => {
    if (data == null) {
      return
    }

    if (typeof data === 'string') {
      findInString(data, searchWord)
      return
    }

    if (Array.isArray(data)) {
      data.forEach((item) => traverse(item))
      return
    }

    if (typeof data === 'object') {
      Object.values(data).forEach((value) => traverse(value))
    }
  }

  traverse(obj)
  return Array.from(sentences)
}

export function getArticleTitle(s) {
  // eslint-disable-next-line prefer-destructuring
  const [path] = s[0]
  return path.split('/')[1]
}

export function splitPascalCase(str) {
  // eslint-disable-next-line prefer-named-capture-group
  return str.replace(/([A-Z])/gu, ' $1').trim()
}

const STORAGE_KEYS = {
  RECENT: 'RECENT_STORAGE_KEY',
  FAVORITE: 'FAVORITE_STORAGE_KEY'
}

export function areItemsEqual(item1, item2) {
  if (item1.path !== item2.path) {
    return false
  }

  const areArraysEqual = (arr1 = [], arr2 = []) => {
    if (arr1.length !== arr2.length) {
      return false
    }
    return arr1.every((item, index) => item === arr2[index])
  }

  return (
    areArraysEqual(item1.names, item2.names) &&
    areArraysEqual(item1.previews, item2.previews)
  )
}

export function saveToStorage(key, items) {
  try {
    const deduplicatedItems = items.reduce((acc, current) => {
      const exists = acc.some((item) =>
        this.call('areItemsEqual', item, current)
      )
      if (!exists) {
        acc.push(current)
      }
      return acc
    }, [])

    window.localStorage.setItem(key, JSON.stringify(deduplicatedItems))
  } catch (error) {
    console.error(`Failed to save to localStorage: ${error}`)
  }
}

export function addSearch(value, type) {
  if (!value?.path) {
    console.error('Invalid search item provided')
    return
  }

  const { recents, favorites } = this.call('getSearches')

  if (type === 'favorite') {
    const newRecents = recents.filter(
      (item) => !this.call('areItemsEqual', item, value)
    )
    const newFavorites = [...favorites, value]

    this.call('saveToStorage', STORAGE_KEYS.RECENT, newRecents)
    this.call('saveToStorage', STORAGE_KEYS.FAVORITE, newFavorites)
  } else {
    const isInFavorites = favorites.some((item) =>
      this.call('areItemsEqual', item, value)
    )
    if (!isInFavorites) {
      const newRecents = [...recents, value]
      this.call('saveToStorage', STORAGE_KEYS.RECENT, newRecents)
    }
  }
}

export function removeSearch(value, type) {
  if (!value?.path) {
    console.error('Invalid search item provided')
    return
  }

  const { recents, favorites } = this.call('getSearches')

  if (type === 'favorite') {
    const newFavorites = favorites.filter(
      (item) => !this.call('areItemsEqual', item, value)
    )
    const newRecents = [...recents, value]

    this.call('saveToStorage', STORAGE_KEYS.FAVORITE, newFavorites)
    this.call('saveToStorage', STORAGE_KEYS.RECENT, newRecents)
  } else {
    const newFavorites = favorites.filter(
      (item) => !this.call('areItemsEqual', item, value)
    )
    const newRecents = recents.filter(
      (item) => !this.call('areItemsEqual', item, value)
    )

    this.call('saveToStorage', STORAGE_KEYS.FAVORITE, newFavorites)
    this.call('saveToStorage', STORAGE_KEYS.RECENT, newRecents)
  }
}

export function getSearches() {
  try {
    const recents = JSON.parse(
      window.localStorage.getItem(STORAGE_KEYS.RECENT) || '[]'
    )
    const favorites = JSON.parse(
      window.localStorage.getItem(STORAGE_KEYS.FAVORITE) || '[]'
    )
    return { recents, favorites }
  } catch (error) {
    console.error(`Failed to retrieve searches: ${error}`)
    return { recents: [], favorites: [] }
  }
}
